import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageSquare, Instagram, Facebook, Mail, Phone, Globe } from "lucide-react";

interface DeveloperProfileCardProps {
  name: string;
  role: string;
  image: string;
  contactActions: {
    onWhatsApp: () => void;
    onInstagram: () => void;
    onFacebook: () => void;
    onEmail: () => void;
    onPhone: () => void;
    onWebsite: () => void;
  };
}

const DeveloperProfileCard: React.FC<DeveloperProfileCardProps> = ({
  name,
  role,
  image,
  contactActions
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="card-3d-container"
    >
      <Card className="developer-card card-glow-intense relative overflow-hidden">
        <div className="card-corners">
          <span></span>
        </div>
        
        <div className="card-tech-overlay"></div>

        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-nalc-teal/50 shadow-lg">
              <img 
                src={image} 
                alt={name} 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-nalc-teal/30 to-transparent"></div>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-semibold">{name}</h3>
              <p className="text-sm text-muted-foreground">Software Engineer and Expert in the Development of Dynamic Technologies</p>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-medium text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Connect With Me
            </h3>
            
            <div className="grid grid-cols-3 gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex flex-col items-center justify-center h-20 hover:bg-nalc-teal/10 card-scan"
                onClick={contactActions.onWhatsApp}
              >
                <MessageSquare className="h-6 w-6 mb-2 text-green-500" />
                <span className="text-xs">WhatsApp</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="flex flex-col items-center justify-center h-20 hover:bg-nalc-teal/10"
                onClick={contactActions.onInstagram}
              >
                <Instagram className="h-6 w-6 mb-2 text-pink-500" />
                <span className="text-xs">Instagram</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="flex flex-col items-center justify-center h-20 hover:bg-nalc-teal/10"
                onClick={contactActions.onFacebook}
              >
                <Facebook className="h-6 w-6 mb-2 text-blue-600" />
                <span className="text-xs">Facebook</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="flex flex-col items-center justify-center h-20 hover:bg-nalc-teal/10"
                onClick={contactActions.onEmail}
              >
                <Mail className="h-6 w-6 mb-2 text-red-500" />
                <span className="text-xs">Email</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="flex flex-col items-center justify-center h-20 hover:bg-nalc-teal/10"
                onClick={contactActions.onPhone}
              >
                <Phone className="h-6 w-6 mb-2 text-nalc-teal" />
                <span className="text-xs">Phone</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                className="flex flex-col items-center justify-center h-20 hover:bg-nalc-teal/10"
                onClick={contactActions.onWebsite}
              >
                <Globe className="h-6 w-6 mb-2 text-blue-500" />
                <span className="text-xs">Website</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default DeveloperProfileCard;
